<template>
  <div
    ref="wrapRef"
    class="pixel-world"
    :class="{ 'pixel-world--preview': preview }"
    tabindex="-1"
    @contextmenu.prevent
    @mousedown="onMouseDown"
    @mousemove="onMouseMove"
    @mouseup="onMouseUp"
    @mouseleave="onMouseLeave"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
    @touchcancel="onTouchEnd"
  >
    <div
      v-if="!preview"
      class="pixel-world__hint"
      :class="{ 'pixel-world__hint--npc': nearbyNpcTarget || nearbyChestTarget }"
    >
      {{ hintText }}
    </div>
    <div ref="pixiHost" class="pixel-world__pixi" />
    <div
      v-if="!preview"
      class="pixel-world__minimap-wrap"
      title="点击快速移动到该区域"
      @click.stop="onMinimapClick"
    >
      <canvas ref="minimapRef" class="pixel-world__minimap" width="140" height="140" />
    </div>
  </div>
</template>

<script>
import {
  MAP_WIDTH,
  MAP_HEIGHT,
  TILE_SIZE,
  TILE,
  MAP_ROWS,
  NPCS,
  PIRATE_NPC,
  MINIMAP_TILE_COLORS,
  npcAt,
  chestAt,
  isWalkable,
  findPath,
  chebyshev,
  getCamera,
  nearbyNpc,
  nearbyChest,
  nearbyCasinoEntrance,
  CASINO_ENTRANCE,
  computeViewport,
  MAX_VIEW_TILES,
  AOI_RADIUS,
  MINIMAP_DIAMETER,
  isWithinAoi,
  WATER_FILL_COLOR,
  GUEST_SPAWN
} from '@/game/worldMap'
import { createPixiWorldRenderer } from '@/game/pixiWorldRenderer'

const MOVE_INTERVAL_MS = 120
const MOVE_STEP_MS = 120
const MINIMAP_DRAW_INTERVAL_MS = 160
const SPEECH_MAX_CHARS = 72
const NPC_SPEECH_MAX_CHARS = 140
const KEY_DIRS = {
  KeyW: [0, -1],
  ArrowUp: [0, -1],
  KeyS: [0, 1],
  ArrowDown: [0, 1],
  KeyA: [-1, 0],
  ArrowLeft: [-1, 0],
  KeyD: [1, 0],
  ArrowRight: [1, 0]
}

export default {
  name: 'PixelWorld',
  props: {
    username: { type: String, required: true },
    players: { type: Object, default: () => ({}) },
    /** 用户名 → { text, until }，头顶聊天气泡 */
    speechBubbles: { type: Object, default: () => ({}) },
    /** NPC id → { text, until }，NPC 头顶气泡 */
    npcSpeechBubbles: { type: Object, default: () => ({}) },
    connected: { type: Boolean, default: false },
    interactEnabled: { type: Boolean, default: true },
    /** 首页预览：可本地走动，互动仅触发 interact-npc（由父级弹登录） */
    preview: { type: Boolean, default: false },
    /** 较长边最多可见格数，0 表示用默认（预览约 14，大厅 25） */
    maxViewTiles: { type: Number, default: 0 },
    /** 本地玩家换装（未入网前预览） */
    selfOutfit: { type: Object, default: null },
    /** 动态世界 NPC（海贼王等） */
    worldNpcs: { type: Array, default: () => [] },
    /** 地上宝箱 */
    worldChests: { type: Array, default: () => [] },
    /** 摇杆存在时禁用触摸移动（点击 NPC/宝箱交互仍保留） */
    disableTouch: { type: Boolean, default: false },
    /** 隐藏所有玩家名牌（首页预览用）；preview 模式下恒为隐藏 */
    hideNames: { type: Boolean, default: false }
  },
  emits: ['move', 'interact-npc', 'pickup-chest', 'enter-casino', 'render-failed', 'render-ready'],
  computed: {
    viewTileCap () {
      if (this.maxViewTiles > 0) return this.maxViewTiles
      if (this.preview) return 14
      return MAX_VIEW_TILES
    },
    nearbyNpcTarget () {
      return nearbyNpc(this.localX, this.localY, 1, this.blockingExtraNpcs)
    },
    nearbyChestTarget () {
      return nearbyChest(this.localX, this.localY, this.worldChests)
    },
    nearbyCasinoTarget () {
      return nearbyCasinoEntrance(this.localX, this.localY, 1)
    },
    blockingExtraNpcs () {
      const pirate = this.resolvedPirateNpc
      return pirate ? [pirate] : []
    },
    resolvedPirateNpc () {
      const raw = (this.worldNpcs || []).find(n => n.id === 'pirate')
      if (!raw) return null
      const st = this.pirateState
      return {
        ...PIRATE_NPC,
        x: st?.x ?? raw.x,
        y: st?.y ?? raw.y
      }
    },
    hintText () {
      const chest = this.nearbyChestTarget
      const casino = this.nearbyCasinoTarget
      const npc = this.nearbyNpcTarget
      if (this.preview) {
        if (npc) return `靠近 ${npc.label} · 登录后可互动`
        return 'WASD / 方向键移动 · 登录后进入完整世界'
      }
      if (casino) return `靠近 ${casino.label} · 按空格进入酒馆`
      if (chest) return '靠近宝箱 · 按空格拾取'
      if (npc) {
        if (npc.actionLabel) return `靠近 ${npc.label} · 按空格${npc.actionLabel}`
        return `靠近 ${npc.label} · 按空格互动`
      }
      return 'WASD / 方向键移动 · 左键行走 · 靠近 NPC 按空格互动'
    }
  },
  data () {
    return {
      localX: 25,
      localY: 14,
      pathQueue: [],
      keysDown: new Set(),
      rafId: null,
      canvasW: 0,
      canvasH: 0,
      scale: 1,
      viewTilesW: 1,
      viewTilesH: 1,
      tilePxSize: TILE_SIZE,
      tilePxW: TILE_SIZE,
      tilePxH: TILE_SIZE,
      displayDpr: 1,
      camera: { cx: 0, cy: 0 },
      facing: 'down',
      pathMouseDown: false,
      hoverTile: null,
      renderX: 25,
      renderY: 14,
      selfWalkPhase: 0,
      selfStepAnim: null,
      remoteStates: {},
      renderer: null,
      pixiReady: false,
      animFrame: 0,
      pirateState: null,
      /** 已发送、待服务端确认的自身移动 */
      pendingSelfMoves: [],
      lastServerX: null,
      lastServerY: null,
      lastMinimapDrawAt: 0
    }
  },
  watch: {
    worldNpcs: {
      deep: true,
      handler (val) {
        this.syncPirateAnim(val)
      }
    },
    players: {
      deep: true,
      handler (val) {
        this.syncSelfFromServer(val[this.username])
        this.syncRemoteAnims(val)
      }
    }
  },
  mounted () {
    if (this.preview) {
      this.snapSelfTo(GUEST_SPAWN.x, GUEST_SPAWN.y)
      this._schedulePreviewAutoWalk()
    }
    this.syncFromPlayers()
    this.syncRemoteAnims(this.players)
    this.syncPirateAnim(this.worldNpcs)
    this._resizeObserver = new ResizeObserver(() => this.resize())
    if (this.$refs.wrapRef) this._resizeObserver.observe(this.$refs.wrapRef)
    window.addEventListener('resize', this.resize)
    this._onWindowKeyDown = (e) => {
      if (!this.shouldCaptureKeys()) return
      if (e.code === 'Space') {
        if (!this.preview && !this.interactEnabled) return
        e.preventDefault()
        if (!e.repeat) this.tryInteractNearby()
        return
      }
      if (KEY_DIRS[e.code]) {
        if (e.code.startsWith('Arrow')) e.preventDefault()
        this.onKeyDown(e)
      }
    }
    this._onWindowKeyUp = (e) => {
      if (KEY_DIRS[e.code]) this.onKeyUp(e)
    }
    window.addEventListener('keydown', this._onWindowKeyDown)
    window.addEventListener('keyup', this._onWindowKeyUp)
    this._moveAcc = 0
    this._lastFrameTime = null
    this._previewLastKick = performance.now()
    if (this.preview) {
      // 独立于 Pixi/rAF 的自愈：每秒检查，若空闲且无计划定时器则重新触发；可见性/尺寸变化时立即恢复
      this._previewWatchdog = setInterval(() => this._previewSelfHeal(), 1000)
      this._onVisibility = () => { if (!document.hidden) this._previewSelfHeal(true) }
      document.addEventListener('visibilitychange', this._onVisibility)
      window.addEventListener('focus', this._onVisibility)
      window.addEventListener('pageshow', this._onVisibility)
    }
    this.bootPixi()
  },
  beforeUnmount () {
    this._destroyed = true
    if (this._resizeObserver) this._resizeObserver.disconnect()
    window.removeEventListener('resize', this.resize)
    window.removeEventListener('keydown', this._onWindowKeyDown)
    window.removeEventListener('keyup', this._onWindowKeyUp)
    if (this.rafId) cancelAnimationFrame(this.rafId)
    if (this._previewIdleTimer) clearTimeout(this._previewIdleTimer)
    if (this._previewWatchdog) clearInterval(this._previewWatchdog)
    if (this._bootRetry) clearTimeout(this._bootRetry)
    if (this._bootTimer) clearTimeout(this._bootTimer)
    if (this._onVisibility) {
      document.removeEventListener('visibilitychange', this._onVisibility)
      window.removeEventListener('focus', this._onVisibility)
      window.removeEventListener('pageshow', this._onVisibility)
    }
    if (this.renderer) {
      this.renderer.destroy()
      this.renderer = null
    }
  },
  methods: {
    webglAvailable () {
      try {
        const c = document.createElement('canvas')
        return !!(c.getContext('webgl2') || c.getContext('webgl') || c.getContext('experimental-webgl'))
      } catch (e) {
        return false
      }
    },
    async bootPixi () {
      if (!this.$refs.pixiHost) return
      if (this.preview && !this._bootTimer) {
        // 预览：8s 内 Pixi 仍未就绪（无 WebGL / 加载挂起）则通知父级切换 Canvas2D 兜底
        this._bootTimer = setTimeout(() => { if (!this.pixiReady && !this._destroyed) this.$emit('render-failed') }, 8000)
        if (!this.webglAvailable()) {
          this.$emit('render-failed')
          return
        }
      }
      try {
        this.renderer = await createPixiWorldRenderer(this.$refs.pixiHost)
        if (this._destroyed) {
          this.renderer.destroy()
          this.renderer = null
          return
        }
        this.pixiReady = true
        if (this._bootTimer) clearTimeout(this._bootTimer)
        this.$emit('render-ready')
        this.resize()
        this.renderer.onTick((time) => this.draw(time))
      } catch (err) {
        console.error('[PixelWorld] Pixi 初始化失败', err)
        if (this.preview && !this._destroyed && (this._bootTries = (this._bootTries || 0) + 1) <= 3) {
          this._bootRetry = setTimeout(() => this.bootPixi(), 1500 * this._bootTries)
        } else if (this.preview && !this._destroyed) {
          this.$emit('render-failed')
        }
      }
    },
    shouldCaptureKeys () {
      const el = document.activeElement
      if (!el) return true
      const tag = el.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return false
      if (el.isContentEditable) return false
      return true
    },
    syncFromPlayers () {
      const me = this.players[this.username]
      if (me && Number.isFinite(me.x) && Number.isFinite(me.y)) {
        this.pendingSelfMoves = []
        this.lastServerX = me.x
        this.lastServerY = me.y
        this.localX = me.x
        this.localY = me.y
        this.renderX = me.x
        this.renderY = me.y
      }
    },
    isSelfLocomoting () {
      return this.keysDown.size > 0 || this.pathQueue.length > 0 || this.selfStepAnim != null
    },
    snapSelfTo (x, y) {
      this.localX = x
      this.localY = y
      this.selfStepAnim = null
      this.renderX = x
      this.renderY = y
      this.selfWalkPhase = 0
    },
    ackPendingSelfMoves (sx, sy) {
      while (
        this.pendingSelfMoves.length > 0 &&
        this.pendingSelfMoves[0].x === sx &&
        this.pendingSelfMoves[0].y === sy
      ) {
        this.pendingSelfMoves.shift()
      }
      const last = this.pendingSelfMoves[this.pendingSelfMoves.length - 1]
      if (last && last.x === sx && last.y === sy) {
        this.pendingSelfMoves = []
      }
      if (this.localX === sx && this.localY === sy) {
        this.pendingSelfMoves = []
      }
    },
    syncSelfFromServer (me) {
      if (!me || !Number.isFinite(me.x) || !Number.isFinite(me.y)) return

      const sx = me.x
      const sy = me.y

      if (sx === this.localX && sy === this.localY) {
        this.pendingSelfMoves = []
        this.lastServerX = sx
        this.lastServerY = sy
        return
      }

      const serverUnchanged =
        this.lastServerX === sx &&
        this.lastServerY === sy

      this.ackPendingSelfMoves(sx, sy)

      if (this.pendingSelfMoves.length > 0) {
        const head = this.pendingSelfMoves[0]
        if (
          !this.isSelfLocomoting() &&
          serverUnchanged &&
          head.x === this.localX &&
          head.y === this.localY &&
          chebyshev(sx, sy, this.localX, this.localY) === 1
        ) {
          this.pendingSelfMoves = []
          this.snapSelfTo(sx, sy)
        }
        this.lastServerX = sx
        this.lastServerY = sy
        return
      }

      this.lastServerX = sx
      this.lastServerY = sy

      if (!this.isSelfLocomoting()) {
        this.snapSelfTo(sx, sy)
      }
    },
    facingFromDelta (dx, dy, fallback = 'down') {
      if (dx > 0) return 'right'
      if (dx < 0) return 'left'
      if (dy > 0) return 'down'
      if (dy < 0) return 'up'
      return fallback
    },
    walkPhaseFromProgress (t) {
      if (t >= 1) return 0
      return 1 + Math.floor(t * 2)
    },
    startSelfStep (fromX, fromY, toX, toY) {
      const now = performance.now()
      const fromRenderX = this.selfStepAnim ? this.renderX : fromX
      const fromRenderY = this.selfStepAnim ? this.renderY : fromY
      this.selfStepAnim = {
        fromX: fromRenderX,
        fromY: fromRenderY,
        toX,
        toY,
        startTime: now,
        duration: MOVE_STEP_MS
      }
    },
    advanceAnim (anim) {
      if (!anim) return null
      const t = Math.min(1, (performance.now() - anim.startTime) / anim.duration)
      const eased = t * t * (3 - 2 * t)
      return {
        x: anim.fromX + (anim.toX - anim.fromX) * eased,
        y: anim.fromY + (anim.toY - anim.fromY) * eased,
        walkPhase: this.walkPhaseFromProgress(t),
        done: t >= 1
      }
    },
    updateAnimations () {
      if (this.selfStepAnim) {
        const step = this.advanceAnim(this.selfStepAnim)
        if (step) {
          this.renderX = step.x
          this.renderY = step.y
          this.selfWalkPhase = step.walkPhase
          if (step.done) this.selfStepAnim = null
        }
      } else {
        this.renderX = this.localX
        this.renderY = this.localY
        this.selfWalkPhase = 0
      }

      for (const st of Object.values(this.remoteStates)) {
        if (!st.anim) {
          st.renderX = st.x
          st.renderY = st.y
          st.walkPhase = 0
          continue
        }
        const step = this.advanceAnim(st.anim)
        if (step) {
          st.renderX = step.x
          st.renderY = step.y
          st.walkPhase = step.walkPhase
          if (step.done) st.anim = null
        }
      }

      if (this.pirateState?.anim) {
        const step = this.advanceAnim(this.pirateState.anim)
        if (step) {
          this.pirateState.renderX = step.x
          this.pirateState.renderY = step.y
          if (step.done) this.pirateState.anim = null
        }
      } else if (this.pirateState) {
        this.pirateState.renderX = this.pirateState.x
        this.pirateState.renderY = this.pirateState.y
      }
    },
    syncPirateAnim (npcs = this.worldNpcs) {
      const raw = (npcs || []).find(n => n.id === 'pirate')
      if (!raw || !Number.isFinite(raw.x) || !Number.isFinite(raw.y)) return
      const now = performance.now()
      const st = this.pirateState
      if (!st) {
        this.pirateState = {
          x: raw.x,
          y: raw.y,
          renderX: raw.x,
          renderY: raw.y,
          facing: raw.facing || 'down',
          walkPhase: raw.walkPhase || 0,
          anim: null
        }
        return
      }
      if (st.x !== raw.x || st.y !== raw.y) {
        const fromX = st.anim ? st.renderX : st.x
        const fromY = st.anim ? st.renderY : st.y
        st.facing = raw.facing || this.facingFromDelta(raw.x - st.x, raw.y - st.y, st.facing)
        st.walkPhase = raw.walkPhase || 0
        st.anim = {
          fromX,
          fromY,
          toX: raw.x,
          toY: raw.y,
          startTime: now,
          duration: 450
        }
        st.x = raw.x
        st.y = raw.y
      } else {
        st.facing = raw.facing || st.facing
        st.walkPhase = raw.walkPhase || 0
      }
    },
    syncRemoteAnims (players = this.players) {
      const now = performance.now()
      const seen = new Set()
      for (const [user, pos] of Object.entries(players || {})) {
        if (user === this.username) continue
        if (!Number.isFinite(pos.x) || !Number.isFinite(pos.y)) continue
        seen.add(user)
        const st = this.remoteStates[user]
        if (!st) {
          this.remoteStates[user] = {
            x: pos.x,
            y: pos.y,
            renderX: pos.x,
            renderY: pos.y,
            facing: 'down',
            walkPhase: 0,
            anim: null
          }
          continue
        }
        if (st.x !== pos.x || st.y !== pos.y) {
          const fromX = st.anim ? st.renderX : st.x
          const fromY = st.anim ? st.renderY : st.y
          st.facing = this.facingFromDelta(pos.x - st.x, pos.y - st.y, st.facing)
          st.anim = {
            fromX,
            fromY,
            toX: pos.x,
            toY: pos.y,
            startTime: now,
            duration: MOVE_STEP_MS
          }
          st.x = pos.x
          st.y = pos.y
        }
      }
      for (const user of Object.keys(this.remoteStates)) {
        if (!seen.has(user)) delete this.remoteStates[user]
      }
    },
    updateCamera () {
      this.camera = getCamera(this.renderX, this.renderY, this.viewTilesW, this.viewTilesH)
    },
    resize () {
      const wrap = this.$refs.wrapRef
      if (!wrap) return
      const rect = wrap.getBoundingClientRect()
      if (rect.width < 32 || rect.height < 32) return

      const cssW = Math.max(1, Math.floor(rect.width))
      const cssH = Math.max(1, Math.floor(rect.height))
      const vp = computeViewport(cssW, cssH, this.viewTileCap)
      this.displayDpr = window.devicePixelRatio || 1
      this.scale = vp.scale
      this.viewTilesW = vp.viewW
      this.viewTilesH = vp.viewH
      this.tilePxSize = vp.tilePx
      this.canvasW = cssW
      this.canvasH = cssH
      this.tilePxW = cssW / this.viewTilesW
      this.tilePxH = cssH / this.viewTilesH
      if (this.renderer) this.renderer.resize(cssW, cssH)
      this.updateCamera()
    },
    occupiedExceptSelf () {
      const counts = new Map()
      for (const [user, pos] of Object.entries(this.players)) {
        if (user === this.username) continue
        const key = `${pos.x},${pos.y}`
        counts.set(key, (counts.get(key) || 0) + 1)
      }
      return counts
    },
    tryMoveTo (nx, ny) {
      if (!this.preview && !this.connected) return false
      const occupied = this.occupiedExceptSelf()
      if (!isWalkable(nx, ny, occupied, this.blockingExtraNpcs)) return false
      if (chebyshev(this.localX, this.localY, nx, ny) !== 1) return false
      const dx = nx - this.localX
      const dy = ny - this.localY
      if (dx > 0) this.facing = 'right'
      else if (dx < 0) this.facing = 'left'
      else if (dy > 0) this.facing = 'down'
      else if (dy < 0) this.facing = 'up'
      this.startSelfStep(this.localX, this.localY, nx, ny)
      this.localX = nx
      this.localY = ny
      if (!this.preview) {
        this.pendingSelfMoves.push({ x: nx, y: ny })
        this.$emit('move', { x: nx, y: ny })
      }
      return true
    },
    tickMovement () {
      if (this.keysDown.size) {
        let cdx = 0
        let cdy = 0
        for (const code of this.keysDown) {
          const dir = KEY_DIRS[code]
          if (dir) { cdx += dir[0]; cdy += dir[1] }
        }
        cdx = Math.sign(cdx)
        cdy = Math.sign(cdy)
        if (cdx !== 0 || cdy !== 0) {
          // 斜向优先，失败后退化为单轴
          if (this.tryMoveTo(this.localX + cdx, this.localY + cdy)) {
            this.pathQueue = []
            return
          }
          if (cdx !== 0 && this.tryMoveTo(this.localX + cdx, this.localY)) {
            this.pathQueue = []
            return
          }
          if (cdy !== 0 && this.tryMoveTo(this.localX, this.localY + cdy)) {
            this.pathQueue = []
            return
          }
        }
        return
      }
      if (this.pathQueue.length) {
        const [nx, ny] = this.pathQueue.shift()
        if (!this.tryMoveTo(nx, ny)) {
          this.pathQueue = []
        }
      }
    },
    onKeyDown (e) {
      if (KEY_DIRS[e.code]) {
        this.keysDown.add(e.code)
        this.pathQueue = []
        if (this.preview) this._schedulePreviewAutoWalk()
      }
    },
    onKeyUp (e) {
      this.keysDown.delete(e.code)
    },
    tryInteractNearby () {
      if (this.tryInteractNearbyNpc()) return true
      if (this.nearbyCasinoTarget && (this.preview || this.interactEnabled)) {
        this.$emit('enter-casino')
        return true
      }
      const chest = this.nearbyChestTarget
      if (chest && (this.preview || this.interactEnabled)) {
        this.$emit('pickup-chest', chest)
        return true
      }
      return false
    },
    tryInteractNearbyNpc () {
      const npc = this.nearbyNpcTarget
      if (!npc) return false
      if (this.preview || this.interactEnabled) {
        this.$emit('interact-npc', npc)
        return true
      }
      return false
    },
    clientToTile (clientX, clientY) {
      const el = this.renderer?.canvas || this.$refs.pixiHost
      if (!el) return null
      const rect = el.getBoundingClientRect()
      if (rect.width < 1 || rect.height < 1) return null
      const x = clientX - rect.left
      const y = clientY - rect.top
      if (!Number.isFinite(x) || !Number.isFinite(y)) return null
      if (x < 0 || y < 0 || x >= rect.width || y >= rect.height) return null
      const { cx, cy } = this.camera
      if (this.tilePxW < 0.001 || this.tilePxH < 0.001) return null
      const mx = Math.floor(x / this.tilePxW + cx)
      const my = Math.floor(y / this.tilePxH + cy)
      if (!Number.isFinite(mx) || !Number.isFinite(my)) return null
      return {
        x: mx,
        y: my,
        canvasPx: { x: Math.round(x), y: Math.round(y) }
      }
    },
    onMouseDown (e) {
      if (e.button !== 0) return
      const tile = this.clientToTile(e.clientX, e.clientY)
      if (tile) {
        const npc = npcAt(tile.x, tile.y, this.blockingExtraNpcs)
        if (npc) {
          const adjacent = chebyshev(this.localX, this.localY, tile.x, tile.y) <= 1
          if (this.preview || adjacent) {
            this.$emit('interact-npc', npc)
            return
          }
        }
        const chest = chestAt(tile.x, tile.y, this.worldChests)
        if (chest) {
          const adjacent = chebyshev(this.localX, this.localY, tile.x, tile.y) <= 1
          if (adjacent && this.interactEnabled) {
            this.$emit('pickup-chest', chest)
            return
          }
        }
      }
      if (this.preview) {
        // 预览模式：允许点击导航，并重置自动行走计时
        if (tile) {
          this.navigateToTile(tile.x, tile.y)
          this._schedulePreviewAutoWalk()
        }
        this.pathMouseDown = true
        return
      }
      this.pathMouseDown = true
      this.setPathFromEvent(e)
    },
    onMouseMove (e) {
      this.hoverTile = this.clientToTile(e.clientX, e.clientY)
      if (this.pathMouseDown) {
        this.setPathFromEvent(e)
      }
    },
    onMouseUp (e) {
      if (e.button === 0) this.pathMouseDown = false
    },
    onMouseLeave () {
      this.pathMouseDown = false
      this.hoverTile = null
    },
    onTouchStart (e) {
      if (e.target.closest('.pixel-world__minimap-wrap')) return
      if (e.touches.length !== 1) return
      // 预览模式不阻止默认行为，保留浏览器滚动能力（pan-y），其余模式仍阻止
      if (!this.preview) e.preventDefault()
      const touch = e.touches[0]
      const tile = this.clientToTile(touch.clientX, touch.clientY)
      if (tile) {
        const npc = npcAt(tile.x, tile.y, this.blockingExtraNpcs)
        if (npc) {
          const adjacent = chebyshev(this.localX, this.localY, tile.x, tile.y) <= 1
          if (this.preview || adjacent) {
            this.$emit('interact-npc', npc)
            return
          }
        }
        const chest = chestAt(tile.x, tile.y, this.worldChests)
        if (chest) {
          const adjacent = chebyshev(this.localX, this.localY, tile.x, tile.y) <= 1
          if (adjacent && this.interactEnabled) {
            this.$emit('pickup-chest', chest)
            return
          }
        }
      }
      if (this.preview) {
        // 预览模式：允许触摸导航，并重置自动行走计时
        if (tile) {
          this.navigateToTile(tile.x, tile.y)
          this._schedulePreviewAutoWalk()
        }
        return
      }
      if (this.disableTouch) return
      this.pathMouseDown = true
      if (tile) this.navigateToTile(tile.x, tile.y)
    },
    onTouchMove (e) {
      if (e.target.closest('.pixel-world__minimap-wrap')) return
      if (e.touches.length !== 1) return
      // 预览模式保留浏览器原生滚动（让用户能回滚主页）
      if (this.preview) return
      e.preventDefault()
      if (!this.pathMouseDown || this.disableTouch) return
      const touch = e.touches[0]
      const tile = this.clientToTile(touch.clientX, touch.clientY)
      if (tile) this.navigateToTile(tile.x, tile.y)
    },
    onTouchEnd () {
      this.pathMouseDown = false
    },
    dpadPress (code) {
      if (KEY_DIRS[code]) {
        this.keysDown.add(code)
        this.pathQueue = []
      }
    },
    dpadRelease (code) {
      this.keysDown.delete(code)
    },
    setPathFromEvent (e) {
      const tile = this.clientToTile(e.clientX, e.clientY)
      if (!tile) return
      this.navigateToTile(tile.x, tile.y)
    },
    resolveWalkableNear (tx, ty, occupied) {
      const extra = this.blockingExtraNpcs
      if (isWalkable(tx, ty, occupied, extra)) return { x: tx, y: ty }
      for (let r = 1; r <= 8; r++) {
        for (let dy = -r; dy <= r; dy++) {
          for (let dx = -r; dx <= r; dx++) {
            if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue
            const nx = tx + dx
            const ny = ty + dy
            if (isWalkable(nx, ny, occupied, extra)) return { x: nx, y: ny }
          }
        }
      }
      return null
    },
    navigateToTile (tx, ty) {
      if (
        !Number.isFinite(this.localX) ||
        !Number.isFinite(this.localY) ||
        !Number.isFinite(tx) ||
        !Number.isFinite(ty)
      ) {
        return
      }
      const occupied = this.occupiedExceptSelf()
      const target = this.resolveWalkableNear(tx, ty, occupied)
      if (!target) return
      const path = findPath(this.localX, this.localY, target.x, target.y, occupied, this.blockingExtraNpcs)
      if (path.length) {
        this.pathQueue = path
        this.keysDown.clear()
        this.pathMouseDown = false
      }
    },
    /** 预览模式：(重)启动空闲自动行走计时，用户操作后延迟 3s 才触发 */
    _schedulePreviewAutoWalk () {
      if (!this.preview) return
      if (this._previewIdleTimer) clearTimeout(this._previewIdleTimer)
      this._previewIdleTimer = setTimeout(() => this._triggerPreviewAutoWalk(), 3000)
    },
    /** 预览模式自愈：长时间无行走则重启；resize 后重新计算视口 */
    _previewSelfHeal (force) {
      if (!this.preview || this._destroyed) return
      if (force) this.resize()
      const idleFor = performance.now() - (this._previewLastKick || 0)
      if (!this.isSelfLocomoting() && (force || idleFor > 5000)) this._triggerPreviewAutoWalk()
    },
    /** 预览模式：随机挑一个可行走的邻近格并导航过去 */
    _triggerPreviewAutoWalk () {
      if (!this.preview || this._destroyed) return
      let pathLen = 0
      try {
        const offsets = [
          [4, 0], [-4, 0], [0, 4], [0, -4],
          [3, 3], [-3, 3], [3, -3], [-3, -3],
          [5, 2], [-5, 2], [5, -2], [-5, -2],
          [7, 0], [-7, 0], [0, 6], [0, -6]
        ]
        const occupied = this.occupiedExceptSelf()
        const shuffled = offsets.slice().sort(() => Math.random() - 0.5)
        for (const [dx, dy] of shuffled) {
          const target = this.resolveWalkableNear(
            this.localX + dx, this.localY + dy, occupied
          )
          if (!target) continue
          const path = findPath(
            this.localX, this.localY,
            target.x, target.y,
            occupied, this.blockingExtraNpcs
          )
          if (path.length > 0) {
            this.pathQueue = path
            pathLen = path.length
            break
          }
        }
      } catch (err) {
        console.error('[PixelWorld] preview auto-walk failed', err)
      }
      this._previewLastKick = performance.now()
      // 无论成功与否都继续排下一次，保证随机移动不会中断；走完路径后只停顿约 1.2s
      if (this._previewIdleTimer) clearTimeout(this._previewIdleTimer)
      const delay = pathLen * MOVE_INTERVAL_MS + (pathLen ? 1200 : 600)
      this._previewIdleTimer = setTimeout(() => this._triggerPreviewAutoWalk(), delay)
    },
    onMinimapClick (e) {
      if (!this.connected && !this.preview) return
      const canvas = this.$refs.minimapRef
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      if (rect.width < 1 || rect.height < 1) return
      const x = ((e.clientX - rect.left) / rect.width) * canvas.width
      const y = ((e.clientY - rect.top) / rect.height) * canvas.height
      const size = canvas.width
      const tw = size / MINIMAP_DIAMETER
      const th = size / MINIMAP_DIAMETER
      const dx = Math.floor(x / tw) - AOI_RADIUS
      const dy = Math.floor(y / th) - AOI_RADIUS
      if (chebyshev(0, 0, dx, dy) > AOI_RADIUS) return
      const tx = Math.max(0, Math.min(MAP_WIDTH - 1, Math.round(this.renderX) + dx))
      const ty = Math.max(0, Math.min(MAP_HEIGHT - 1, Math.round(this.renderY) + dy))
      this.navigateToTile(tx, ty)
    },
    isInView (mx, my) {
      const { cx, cy } = this.camera
      const pad = 1
      return mx >= cx - pad && mx < cx + this.viewTilesW + pad &&
        my >= cy - pad && my < cy + this.viewTilesH + pad
    },
    depthForTree (my, mx) {
      return 500 + my + mx * 0.0001
    },
    /** 实体（角色/宝箱）深度：Y 越大越靠前 */
    depthForEntity (y, x) {
      return 1000 + y + x * 0.0001 + 0.25
    },
    /** 非树 Objects：可遮挡角色 */
    depthForObject (my, mx) {
      return 2000 + my + mx * 0.0001 + 0.75
    },
    /** 倒挂金色箭头，尖端朝下指向 NPC */
    speechTextFor (name) {
      const bubble = this.speechBubbles[name]
      if (!bubble?.text || bubble.until <= Date.now()) return ''
      const t = String(bubble.text).trim()
      if (t.length <= SPEECH_MAX_CHARS) return t
      return `${t.slice(0, SPEECH_MAX_CHARS - 1)}…`
    },
    npcSpeechBubbleFor (npcId) {
      const bubble = this.npcSpeechBubbles[npcId]
      if (!bubble || bubble.until <= Date.now()) return null
      return bubble
    },
    npcSpeechTextFrom (bubble) {
      const t = String(bubble?.text || '').trim()
      if (!t) return ''
      if (t.length <= NPC_SPEECH_MAX_CHARS) return t
      return `${t.slice(0, NPC_SPEECH_MAX_CHARS - 1)}…`
    },
    playerOutfit (name, isSelf) {
      if (isSelf && this.selfOutfit) return this.selfOutfit
      const pos = this.players[name]
      return pos?.outfit || null
    },
    collectScene () {
      const entities = []
      const overlays = []
      const nearbyNpc = this.nearbyNpcTarget
      const nearbyChest = this.nearbyChestTarget
      const canInteract = this.interactEnabled

      for (const chest of this.worldChests) {
        if (!this.isInView(chest.x, chest.y)) continue
        entities.push({
          key: `chest:${chest.id || `${chest.x},${chest.y}`}`,
          kind: 'chest',
          x: chest.x,
          y: chest.y,
          zIndex: this.depthForEntity(chest.y, chest.x)
        })
        overlays.push({
          key: `chest:${chest.id || `${chest.x},${chest.y}`}`,
          x: chest.x,
          y: chest.y,
          showArrow: canInteract && nearbyChest?.id === chest.id
        })
      }

      if (this.isInView(CASINO_ENTRANCE.x, CASINO_ENTRANCE.y)) {
        overlays.push({
          key: 'casino',
          x: CASINO_ENTRANCE.x,
          y: CASINO_ENTRANCE.y,
          label: CASINO_ENTRANCE.label,
          labelColor: '#ffd54f',
          showArrow: canInteract && !!this.nearbyCasinoTarget
        })
      }

      for (const npc of NPCS) {
        if (!isWithinAoi(this.localX, this.localY, npc.x, npc.y)) continue
        if (!this.isInView(npc.x, npc.y)) continue
        const bubble = this.npcSpeechBubbleFor(npc.id)
        const speech = bubble ? this.npcSpeechTextFrom(bubble) : ''
        const nearby = canInteract && nearbyNpc?.id === npc.id
        entities.push({
          key: `npc:${npc.id}`,
          kind: 'npc',
          x: npc.x,
          y: npc.y,
          zIndex: this.depthForEntity(npc.y, npc.x),
          person: {
            role: npc.sprite || npc.id,
            facing: 'down',
            walkPhase: 0,
            showHat: true
          }
        })
        overlays.push({
          key: `npc:${npc.id}`,
          x: npc.x,
          y: npc.y,
          emoji: npc.emoji,
          label: npc.label,
          showArrow: nearby,
          actionLabel: nearby ? npc.actionLabel : '',
          speech
        })
      }

      const ps = this.pirateState
      const pirateNpc = this.resolvedPirateNpc
      if (pirateNpc && ps) {
        const rx = ps.renderX ?? ps.x
        const ry = ps.renderY ?? ps.y
        if (this.isInView(rx, ry)) {
          const bubble = this.npcSpeechBubbleFor(pirateNpc.id)
          const speech = bubble ? this.npcSpeechTextFrom(bubble) : ''
          const nearby = canInteract && nearbyNpc?.id === pirateNpc.id
          entities.push({
            key: 'npc:pirate',
            kind: 'npc',
            x: rx,
            y: ry,
            zIndex: this.depthForEntity(ry, rx),
            person: {
              role: pirateNpc.sprite || pirateNpc.id,
              facing: ps.facing || 'down',
              walkPhase: ps.walkPhase || 0,
              showHat: true
            }
          })
          overlays.push({
            key: 'npc:pirate',
            x: rx,
            y: ry,
            emoji: pirateNpc.emoji,
            label: pirateNpc.label,
            showArrow: nearby,
            actionLabel: nearby ? pirateNpc.actionLabel : '',
            speech
          })
        }
      }

      for (const [user, pos] of Object.entries(this.players)) {
        if (user === this.username) continue
        const st = this.remoteStates[user]
        const rx = st?.renderX ?? pos.x
        const ry = st?.renderY ?? pos.y
        if (!this.isInView(rx, ry)) continue
        entities.push({
          key: `player:${user}`,
          kind: 'player',
          x: rx,
          y: ry,
          zIndex: this.depthForEntity(ry, rx),
          person: {
            role: 'other',
            username: user,
            facing: st?.facing || 'down',
            walkPhase: st?.walkPhase || 0,
            isSelf: false,
            outfit: this.playerOutfit(user, false)
          }
        })
        overlays.push({
          key: `player:${user}`,
          x: rx,
          y: ry,
          label: (this.preview || this.hideNames) ? '' : user,
          labelColor: '#ffe8cc',
          speech: this.speechTextFor(user),
          speechSelf: false
        })
      }

      if (this.isInView(this.renderX, this.renderY)) {
        entities.push({
          key: `player:${this.username}`,
          kind: 'player',
          x: this.renderX,
          y: this.renderY,
          zIndex: this.depthForEntity(this.renderY, this.renderX),
          person: {
            role: 'self',
            username: this.username,
            facing: this.facing,
            walkPhase: this.selfWalkPhase,
            isSelf: true,
            outfit: this.playerOutfit(this.username, true)
          }
        })
        overlays.push({
          key: `player:${this.username}`,
          x: this.renderX,
          y: this.renderY,
          label: (this.preview || this.hideNames) ? '' : `${this.username} (我)`,
          labelColor: '#dff0ff',
          speech: this.speechTextFor(this.username),
          speechSelf: true
        })
      }

      return {
        camera: this.camera,
        viewTilesW: this.viewTilesW,
        viewTilesH: this.viewTilesH,
        hoverTile: this.pathMouseDown ? this.hoverTile : null,
        animFrame: this.animFrame,
        entities,
        overlays
      }
    },
    drawMinimap () {
      const canvas = this.$refs.minimapRef
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      const size = canvas.width
      const tw = size / MINIMAP_DIAMETER
      const th = size / MINIMAP_DIAMETER
      const centerX = Math.round(this.renderX)
      const centerY = Math.round(this.renderY)
      const R = AOI_RADIUS

      ctx.clearRect(0, 0, size, size)
      ctx.save()
      ctx.beginPath()
      ctx.arc(size / 2, size / 2, size / 2 - 1, 0, Math.PI * 2)
      ctx.clip()

      ctx.fillStyle = 'rgba(0,0,0,0.45)'
      ctx.fillRect(0, 0, size, size)

      for (let dy = -R; dy <= R; dy++) {
        for (let dx = -R; dx <= R; dx++) {
          if (chebyshev(0, 0, dx, dy) > R) continue
          const wx = centerX + dx
          const wy = centerY + dy
          const px = (dx + R) * tw
          const py = (dy + R) * th
          if (wx < 0 || wy < 0 || wx >= MAP_WIDTH || wy >= MAP_HEIGHT) {
            ctx.fillStyle = WATER_FILL_COLOR
          } else {
            ctx.fillStyle = MINIMAP_TILE_COLORS[MAP_ROWS[wy][wx]] || MINIMAP_TILE_COLORS[TILE.GRASS]
          }
          ctx.fillRect(Math.floor(px), Math.floor(py), Math.ceil(tw), Math.ceil(th))
        }
      }

      for (const npc of NPCS) {
        if (!isWithinAoi(centerX, centerY, npc.x, npc.y)) continue
        const dx = npc.x - centerX
        const dy = npc.y - centerY
        const nx = (dx + R) * tw + tw / 2
        const ny = (dy + R) * th + th / 2
        ctx.fillStyle = npc.minimapColor || '#fff'
        ctx.beginPath()
        ctx.arc(nx, ny, Math.max(3, tw * 0.9), 0, Math.PI * 2)
        ctx.fill()
        ctx.strokeStyle = 'rgba(0,0,0,0.6)'
        ctx.lineWidth = 1
        ctx.stroke()
      }

      for (const chest of this.worldChests) {
        if (!isWithinAoi(centerX, centerY, chest.x, chest.y)) continue
        const dx = chest.x - centerX
        const dy = chest.y - centerY
        const cx = (dx + R) * tw + tw / 2
        const cy = (dy + R) * th + th / 2
        ctx.fillStyle = '#f1c40f'
        ctx.fillRect(cx - 2, cy - 2, 4, 4)
      }

      const ps = this.pirateState
      if (ps && Number.isFinite(ps.renderX)) {
        const pxWorld = ps.renderX
        const pyWorld = ps.renderY
        if (isWithinAoi(centerX, centerY, pxWorld, pyWorld)) {
          const dx = pxWorld - centerX
          const dy = pyWorld - centerY
          const px = (dx + R) * tw + tw / 2
          const py = (dy + R) * th + th / 2
          ctx.fillStyle = PIRATE_NPC.minimapColor
          ctx.beginPath()
          ctx.arc(px, py, Math.max(3.5, tw * 0.95), 0, Math.PI * 2)
          ctx.fill()
          ctx.strokeStyle = '#fff'
          ctx.lineWidth = 1
          ctx.stroke()
        }
      }

      for (const [user, pos] of Object.entries(this.players)) {
        if (user === this.username) continue
        if (!isWithinAoi(centerX, centerY, pos.x, pos.y)) continue
        const dx = pos.x - centerX
        const dy = pos.y - centerY
        const px = (dx + R) * tw + tw / 2
        const py = (dy + R) * th + th / 2
        ctx.fillStyle = '#E67E22'
        ctx.beginPath()
        ctx.arc(px, py, Math.max(2.5, tw * 0.65), 0, Math.PI * 2)
        ctx.fill()
      }

      const selfPx = R * tw + tw / 2
      const selfPy = R * th + th / 2
      ctx.fillStyle = '#4A90E2'
      ctx.beginPath()
      ctx.arc(selfPx, selfPy, Math.max(3.5, tw * 0.85), 0, Math.PI * 2)
      ctx.fill()
      ctx.strokeStyle = '#fff'
      ctx.lineWidth = 1.5
      ctx.stroke()

      const { cx, cy } = this.camera
      const vx = (cx - centerX + R) * tw
      const vy = (cy - centerY + R) * th
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)'
      ctx.lineWidth = 1.5
      ctx.strokeRect(vx, vy, this.viewTilesW * tw, this.viewTilesH * th)

      ctx.restore()

      ctx.beginPath()
      ctx.arc(size / 2, size / 2, size / 2 - 1, 0, Math.PI * 2)
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)'
      ctx.lineWidth = 1.5
      ctx.stroke()
    },
    draw (timestamp) {
      if (!this.renderer || !this.pixiReady) return

      if (this._lastFrameTime == null) this._lastFrameTime = timestamp
      const frameDt = Math.min(48, Math.max(0, timestamp - this._lastFrameTime))
      this._lastFrameTime = timestamp
      this._moveAcc = (this._moveAcc || 0) + frameDt
      while (this._moveAcc >= MOVE_INTERVAL_MS) {
        this._moveAcc -= MOVE_INTERVAL_MS
        this.tickMovement()
      }

      // 预览看门狗：若定时器链因任何原因中断（标签页挂起恢复等），空闲超过 6s 自动重启随机行走
      if (this.preview && !this.isSelfLocomoting() && timestamp - (this._previewLastKick || 0) > 6000) {
        this._previewLastKick = timestamp
        this._triggerPreviewAutoWalk()
      }

      this.updateCamera()
      this.syncRemoteAnims()
      this.syncPirateAnim()
      this.updateAnimations()
      this.animFrame = Math.floor(timestamp / 450)

      const scene = this.collectScene()
      this.renderer.sync(scene)
      if (this.preview && this.$refs.wrapRef) {
        // 仅供自动化验证：暴露预览角色位置与可见名牌数量（无副作用）
        const ds = this.$refs.wrapRef.dataset
        ds.previewPos = `${this.renderX.toFixed(2)},${this.renderY.toFixed(2)}`
        ds.previewLabels = String(scene.overlays.filter(o => o.label && String(o.key).startsWith('player:')).length)
      }

      if (!this.preview && timestamp - this.lastMinimapDrawAt >= MINIMAP_DRAW_INTERVAL_MS) {
        this.drawMinimap()
        this.lastMinimapDrawAt = timestamp
      }
    }

  }
}
</script>

<style scoped>
.pixel-world {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  outline: none;
  cursor: crosshair;
  touch-action: none;
}

/* 预览模式（主页）：允许浏览器竖向滚动，使用户能回滚主页 */
.pixel-world--preview {
  touch-action: pan-y;
}

.pixel-world__pixi {
  position: absolute;
  inset: 0;
  z-index: 0;
}
.pixel-world__pixi :deep(canvas) {
  display: block;
  width: 100%;
  height: 100%;
  image-rendering: pixelated;
  image-rendering: crisp-edges;
}

.pixel-world__hint {
  position: absolute;
  top: calc(var(--game-top-bar-height, 53px) + 8px);
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.72rem;
  color: var(--color-hint-text);
  background: var(--color-overlay-hint);
  padding: 4px 10px;
  border-radius: var(--radius-xs);
  pointer-events: none;
  white-space: nowrap;
  z-index: 5;
  transition: background 0.2s, color 0.2s;
}

.pixel-world__hint--npc {
  color: var(--color-on-primary);
  background: var(--color-npc-hint);
}

.pixel-world__minimap-wrap {
  position: absolute;
  right: max(10px, env(safe-area-inset-right, 0px));
  top: calc(var(--game-top-bar-height, 53px) + 8px);
  z-index: 18;
  box-sizing: border-box;
  width: 148px;
  height: 148px;
  aspect-ratio: 1;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-overlay-minimap);
  border: var(--border-width-input) solid var(--color-minimap-border);
  border-radius: 50%;
  box-shadow: var(--shadow-minimap);
  cursor: pointer;
  touch-action: manipulation;
  overflow: hidden;
  transition: border-color var(--duration-fast) ease, box-shadow var(--duration-fast) ease;
}

.pixel-world__minimap-wrap:hover {
  border-color: rgba(255, 255, 255, 0.45);
  box-shadow: var(--shadow-minimap), 0 0 0 1px rgba(255, 255, 255, 0.12);
}

.pixel-world__minimap {
  display: block;
  width: 140px;
  height: 140px;
  image-rendering: pixelated;
  image-rendering: crisp-edges;
}

/* ── 移动端横屏：小地图缩小 ── */
@media (pointer: coarse) and (orientation: landscape) {
  .pixel-world__minimap-wrap {
    width: 88px;
    height: 88px;
    padding: 3px;
  }

  .pixel-world__minimap {
    width: 82px;
    height: 82px;
  }
}
</style>
